export const SITE = {
  name: "DJC Global",
  url: "https://djcglobal.net",
  tagline: "A world of opportunity. Built around your business.",
  description:
    "DJC Global connects businesses with specialized solutions for business savings, employee benefits, healthcare operations, AI marketing, and what comes next.",
  phone: "518-376-6328",
  phoneHref: "tel:+15183766328",
  email: "douglas@djcglobal.net",
  logo: "/djc-logo-sm.png",
};

export const GROUPS = [
  {
    name: "Business Efficiency",
    slug: "business-efficiency",
    blurb: "Find hidden cost and put money back to work.",
  },
  {
    name: "People & Healthcare",
    slug: "people-healthcare",
    blurb: "Strengthen your people and the systems behind them.",
  },
  {
    name: "Marketing & Growth",
    slug: "marketing-growth",
    blurb: "Reach more of the right customers, more often.",
  },
  {
    name: "Emerging Opportunities",
    slug: "emerging-opportunities",
    blurb: "Understand what comes next, before it arrives.",
  },
];

export const SOLUTIONS = [
  {
    id: "fte-advantage",
    num: "01",
    slug: "healthcare-revenue-cycle-staffing",
    name: "Healthcare Revenue-Cycle Staffing",
    group: "People & Healthcare",
    variant: "fullbleed",
    menuLabel: "Specialized staffing for billing, coding, and revenue operations",
    tagline: "More capacity for the work that keeps healthcare moving.",
    short: "Specialized revenue-cycle staffing for hospitals and healthcare organizations.",
    atlasWhat:
      "Connects hospitals and healthcare organizations with experienced revenue-cycle professionals for billing, coding, A/R, denials, credentialing, and prior authorization.",
    helps: "Added capacity across the revenue cycle, with a staffing model built for healthcare economics.",
    audience: "Healthcare executives, finance leaders, and revenue-cycle teams",
    what:
      "Healthcare Revenue-Cycle Staffing connects hospitals and healthcare organizations with experienced revenue-cycle professionals through FTE Advantage. Staffing support covers the functions that keep patient accounting moving — billing, coding, accounts receivable, denials, credentialing, and prior authorization.",
    who:
      "Healthcare executives, finance leaders, and revenue-cycle teams carrying heavy patient-accounting workloads, backlogs, or open roles.",
    challenge:
      "Revenue-cycle work is specialized, volume-driven, and never stops. Unfilled roles stretch billing cycles, age receivables, and pull clinical and administrative staff away from the work only they can do.",
    capabilities: [
      "Billing and coding support",
      "Accounts receivable follow-up",
      "Denials management",
      "Credentialing",
      "Prior authorization",
      "A dedicated full-time-equivalent staffing model with a flat monthly fee — no benefits, payroll-tax, PTO, or recruiting costs for your facility",
    ],
    providerNote:
      "This is a healthcare revenue-cycle staffing solution — not a general employee tax-credit program.",
    djcRole:
      "DJC Global introduces healthcare organizations to FTE Advantage and helps you start the conversation. Staffing decisions, program details, and outcomes are handled entirely by FTE Advantage.",
    providerRole:
      "FTE Advantage provides the staffing model, the people, and day-to-day program management.",
    next: [
      "Share your current revenue-cycle challenges",
      "We connect you with FTE Advantage",
      "Review roles, coverage, and pricing with their team",
      "Decide with full information",
    ],
    faqs: [
      {
        q: "Is this a tax-credit program?",
        a: "No. This is a healthcare revenue-cycle staffing solution — experienced professionals for billing, coding, accounts receivable, denials, credentialing, and prior authorization, delivered by FTE Advantage.",
      },
      {
        q: "Can we start with just one function?",
        a: "That is a conversation to have with FTE Advantage. Many organizations begin with the function under the most pressure. Ask them directly through the referral link or an inquiry.",
      },
      {
        q: "How is staffing priced?",
        a: "FTE Advantage describes a flat monthly fee per full-time-equivalent staff member. Confirm current pricing and terms with their team before making decisions.",
      },
    ],
    cta: { label: "Explore FTE Advantage", url: "https://fteadvantage.com/home-c5r" },
    provider: "FTE Advantage",
    image:
      "https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Two healthcare professionals reviewing information together on a tablet",
  },
  {
    id: "ehp",
    num: "02",
    slug: "employee-health-benefits",
    name: "Employee Health & Benefits",
    group: "People & Healthcare",
    variant: "split",
    menuLabel: "Preventive-care benefits for eligible employers",
    tagline: "Explore better support for your people.",
    short: "Preventive-care benefits with potential payroll-related savings for eligible employers.",
    atlasWhat:
      "Introduces eligible employers to EHP — a modern preventive-care benefit designed to complement existing coverage, with potential payroll-related savings where terms allow.",
    helps: "A benefit employees can feel day to day, designed to work alongside the coverage you already have.",
    audience: "Business owners, HR teams, and benefits decision-makers",
    what:
      "Employee Health & Benefits introduces eligible employers to EHP, a modern preventive-care benefit designed to complement existing health coverage. Depending on eligibility, the program structure may also create potential payroll-related savings.",
    who:
      "Business owners, HR teams, and benefits decision-makers. EHP's current materials describe the program for employers with five or more W-2 employees.",
    challenge:
      "Benefits costs keep climbing while employees want more visible, everyday value from their coverage. Many employers assume the only lever is switching plans.",
    capabilities: [
      "Preventive-care benefit for employees and enrolled family members",
      "Designed to complement existing health coverage — no plan switch required",
      "Potential payroll-related savings where eligibility and program terms allow",
    ],
    providerNote:
      "Any savings depend on individual eligibility and current program terms. DJC Global does not provide tax, legal, or compliance advice — verify details with EHP and your own advisors.",
    djcRole:
      "DJC Global connects interested employers with EHP. Eligibility, administration, and program terms are determined by EHP.",
    providerRole:
      "EHP delivers the benefit program, determines eligibility, and administers the details.",
    next: [
      "Share the size of your team and what you want to improve",
      "We connect you with EHP",
      "Verify eligibility and current program terms with their team",
      "Decide with full information",
    ],
    faqs: [
      {
        q: "Does this replace our current health insurance?",
        a: "No. EHP is designed to complement existing coverage where applicable — employees generally keep their current plans.",
      },
      {
        q: "Are the payroll savings guaranteed?",
        a: "No. Any savings depend on eligibility, participation, and current program terms. Confirm the specifics with EHP before making decisions.",
      },
      {
        q: "Who is eligible?",
        a: "EHP's current materials describe the program for employers with five or more W-2 employees. Verify current eligibility with EHP.",
      },
    ],
    cta: { label: "Explore Employee Benefits", url: "https://ehpinc.com/?aff=103451" },
    provider: "EHP",
    image:
      "https://images.unsplash.com/photo-1758691737584-a8f17fb34475?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Colleagues celebrating together in a bright, modern office",
  },
  {
    id: "sixthshock",
    num: "03",
    slug: "enterprise-digital-asset-strategy",
    name: "Enterprise & Digital Asset Strategy",
    group: "Emerging Opportunities",
    variant: "band",
    menuLabel: "Corporate structuring and digital asset strategy",
    tagline: "Explore what's next for your business.",
    short: "Enterprise consulting in corporate structuring and digital asset strategy.",
    atlasWhat:
      "Introduces business leaders to SixthShock — enterprise consulting in corporate structuring and digital asset strategy, with a compliance-first approach to real-world-asset tokenization.",
    helps: "A structured, compliance-first way to evaluate corporate structure and digital asset strategy.",
    audience: "Business leaders evaluating structure, capital formation, or digital assets",
    what:
      "SixthShock provides enterprise consulting in corporate structuring and digital asset strategy — advising companies preparing to participate in the tokenization of real-world assets, with a focus on SEC-regulated, compliance-first offerings.",
    who:
      "Business leaders evaluating corporate structure, capital formation, or digital asset strategy — and the obligations that come with each.",
    challenge:
      "Digital asset strategy touches regulation, structure, and reputation at the same time. Entering it without the right foundation creates avoidable risk.",
    capabilities: [
      "Corporate structuring for investment offerings",
      "Digital asset strategy grounded in real businesses and assets",
      "Guidance for navigating SEC-regulated offering requirements",
      "End-to-end coordination from structure to launch",
    ],
    providerNote:
      "SixthShock's work centers on real businesses and assets within regulatory frameworks — not speculative trading. Details are described in their current materials.",
    djcRole:
      "DJC Global introduces interested business leaders to SixthShock. All advisory work, opinions, and outcomes are delivered by SixthShock.",
    providerRole:
      "SixthShock provides the consulting, structuring, and strategy work end to end.",
    next: [
      "Share what stage your business is at",
      "We connect you with SixthShock",
      "Review approach and fit with their team",
      "Decide with full information",
    ],
    faqs: [
      {
        q: "Is this about cryptocurrency trading?",
        a: "No. SixthShock's work centers on corporate structuring and the tokenization of real businesses and assets within SEC-regulated frameworks — not speculative trading.",
      },
      {
        q: "Is this right for a smaller business?",
        a: "Start by asking. SixthShock works with companies at different stages; a conversation with their team is the right way to gauge fit.",
      },
    ],
    cta: { label: "Explore SixthShock", url: "https://sixthshock.com/r/SS-8XNQNX3H" },
    provider: "SixthShock",
    image:
      "https://images.unsplash.com/photo-1493397212122-2b85dda8106b?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Abstract modern building facade with sweeping curved lines against a blue sky",
  },
  {
    id: "tariff-recovery",
    num: "04",
    slug: "tariff-recovery-review",
    name: "Tariff Recovery Review",
    group: "Business Efficiency",
    variant: "fullbleed",
    menuLabel: "A closer look at your import tariff costs",
    tagline: "Start with a closer look at your tariff costs.",
    short: "A professional review of tariff payments for importers and customs brokers.",
    atlasWhat:
      "Connects importers with Tariff Recovery Corp for a review of tariff payments — including duties tied to IEEPA tariffs — starting with a free claim review and refund calculation.",
    helps: "A clearer picture of whether tariff payments are worth reviewing, before any commitment.",
    audience: "Importers and the customs brokers who support them",
    what:
      "Tariff Recovery Corp helps importers and customs brokers review tariff payments — including duties tied to IEEPA tariffs — with a free claim review and refund calculation, full-service claim preparation, and filing support.",
    who:
      "Businesses that have paid U.S. import duties, and the customs brokers who support them.",
    challenge:
      "Import duties are complex and move with trade policy. Most businesses have no easy way to know whether money paid in tariffs could still be recovered.",
    capabilities: [
      "Free claim review and refund calculation",
      "Full-service claim preparation and filing support",
      "Management of the claim process end to end",
      "Access to advance funding options where available",
    ],
    providerNote:
      "Eligibility, mechanisms, and outcomes depend on the provider's review and current law. DJC Global makes no representations about refunds, timelines, or amounts — discuss specifics directly with Tariff Recovery Corp.",
    djcRole:
      "DJC Global connects interested businesses with Tariff Recovery Corp. The review, filing, and any recovery are handled entirely by the provider.",
    providerRole:
      "Tariff Recovery Corp performs the claim review, preparation, and filing support.",
    next: [
      "Share that you import and want a tariff review",
      "We connect you with Tariff Recovery Corp",
      "Complete their free claim review and refund calculation",
      "Decide with full information",
    ],
    faqs: [
      {
        q: "Does a review commit us to anything?",
        a: "Tariff Recovery Corp describes the initial claim review and refund calculation as free. Confirm scope and terms with them before proceeding.",
      },
      {
        q: "Is a refund guaranteed?",
        a: "No. Outcomes depend on the provider's review and the applicable rules. Treat any recovery as possible, not promised.",
      },
    ],
    cta: { label: "Explore Tariff Recovery", url: "https://tariffrecoverycorp.com/r/djcglobal" },
    provider: "Tariff Recovery Corp",
    image:
      "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Container ships docked at a busy commercial port",
  },
  {
    id: "tech-savings",
    num: "05",
    slug: "technology-cost-optimization",
    name: "Technology Cost Optimization",
    group: "Business Efficiency",
    variant: "split",
    menuLabel: "Evaluate cloud, connectivity, and security spend",
    tagline: "Make your technology budget work harder.",
    short: "Evaluate technology spending and compare options across your stack.",
    atlasWhat:
      "Helps businesses evaluate technology spending and compare solutions across cloud, connectivity, voice, collaboration, security, and infrastructure — with a shortlist of best-fit options.",
    helps: "Market visibility on technology spend, a shortlist of options, and support through negotiation.",
    audience: "Leaders facing renewals, migrations, or spend that no longer matches needs",
    what:
      "Technology Cost Optimization connects businesses with a technology advisory service that evaluates spending and compares solutions across cloud, connectivity, voice, collaboration, security, and infrastructure. The process starts with a short conversation about your environment and goals, moves to supplier research and a shortlist of best-fit options, and continues through selection, negotiation, and implementation.",
    who:
      "Business leaders who suspect their technology spend no longer matches their needs — or who are facing renewals, migrations, or growth.",
    challenge:
      "Technology contracts accumulate quietly. Without market visibility, it is hard to know whether you are overpaying or missing better-fitting options.",
    capabilities: [
      "Cloud, connectivity, voice, and collaboration review",
      "Security and infrastructure assessment",
      "Supplier research and a shortlist of best-fit options",
      "Selection, negotiation, and implementation support",
    ],
    providerNote:
      "The referral destination currently presents HonestStok-related technology advisory services. Provider branding is being confirmed before it is named in detailed site copy.",
    djcRole:
      "DJC Global connects you with the advisory service. The review, recommendations, and implementation are delivered by the provider.",
    providerRole:
      "The provider researches suppliers, presents options, negotiates terms, and supports implementation.",
    next: [
      "Share the area of technology spend you want reviewed",
      "We connect you with the advisory provider",
      "Walk through your environment on a short call",
      "Review the shortlist and decide",
    ],
    faqs: [
      {
        q: "Do we have to change providers?",
        a: "Not necessarily. The review may confirm your current setup is right. Any change is your decision after seeing the options.",
      },
      {
        q: "How does the advisory get paid?",
        a: "Confirm compensation details with the provider during your conversation — and see our referral disclosure for how DJC Global is compensated.",
      },
    ],
    cta: { label: "Explore Technology Savings", url: "https://ultimatetechsavings.com/?ref=DJCGLOBAL#" },
    provider: "Ultimate Tech Savings",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Organized network cabling inside a data center",
  },
  {
    id: "prosperlink",
    num: "06",
    slug: "emerging-technology-opportunities",
    name: "Emerging Technology Opportunities",
    group: "Emerging Opportunities",
    variant: "band",
    menuLabel: "A separate pathway for exploring emerging tech",
    tagline: "Understand the opportunity before taking the next step.",
    short: "A separate pathway for exploring emerging technology platforms.",
    atlasWhat:
      "A separate exploration pathway: ProsperLink is an emerging-technology platform focused on blockchain infrastructure and node sales, accessed through a ProsperLink account.",
    helps: "Direct access to ProsperLink's current materials — evaluate the opportunity on the platform itself.",
    audience: "Those actively exploring emerging technology, prepared to evaluate it independently",
    what:
      "ProsperLink is an emerging-technology platform focused on blockchain infrastructure and node sales. Access is provided through a ProsperLink account — visitors create a profile, verify their email, and review current opportunities directly on the platform.",
    who:
      "Businesses and individuals actively exploring emerging technology — and prepared to evaluate it independently.",
    challenge:
      "Emerging technology moves faster than clear public information. The risk is not missing the opportunity — it is committing before understanding it.",
    capabilities: [
      "A ProsperLink account with email verification",
      "Direct access to the platform's current materials and opportunities",
    ],
    providerNote:
      "This is a separate exploration pathway, not a routine cost-saving service. DJC Global cannot advise on node economics, projected income, returns, or liquidity. Review ProsperLink's materials carefully and consult your own advisors before committing funds.",
    djcRole:
      "DJC Global provides the connection only. All platform details, terms, and risks are explained by ProsperLink.",
    providerRole:
      "ProsperLink operates the platform and explains every detail of participation.",
    next: [
      "Decide if this pathway is right for you now",
      "Create a ProsperLink account through the referral link",
      "Verify your email and review their materials in full",
      "Consult your own advisors before committing funds",
    ],
    faqs: [
      {
        q: "Is this a cost-saving service like the others?",
        a: "No. This is a distinct exploration pathway for emerging technology. Start only if that is what you are looking for.",
      },
      {
        q: "Can DJC Global explain the economics?",
        a: "No. DJC Global provides the connection, not the advice. All platform details, terms, and risks are explained by ProsperLink — evaluate them independently.",
      },
    ],
    cta: { label: "Explore ProsperLink", url: "https://app.prosperlink.co/?ref=dougclo5003631099" },
    provider: "ProsperLink",
    image:
      "https://images.unsplash.com/photo-1640161704729-cbe966a08476?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Physical bitcoin and ethereum coins representing blockchain technology",
  },
  {
    id: "veluro",
    num: "07",
    slug: "ai-marketing-business-growth",
    name: "AI Marketing & Business Growth",
    group: "Marketing & Growth",
    variant: "fullbleed",
    menuLabel: "AI-powered prospecting, campaigns, and follow-up",
    tagline: "Give your marketing more power. Give yourself more time.",
    short: "AI-powered marketing — from prospecting to follow-up.",
    atlasWhat:
      "Introduces businesses to Veluro — an AI-powered marketing platform that finds high-intent prospects, creates campaigns and content, handles follow-up, and monitors your pipeline. Vela is Veluro's AI growth agent.",
    helps: "Prospecting, campaigns, content, and follow-up in one place — so fewer leads slip through.",
    audience: "Owners and growth teams who need momentum without a full in-house team",
    what:
      "Veluro is an AI-powered marketing platform that finds high-intent prospects, creates and manages campaigns and content, handles follow-up, and monitors the health of your business. Vela, Veluro's AI growth agent, works alongside you across channels.",
    who:
      "Owners and growth teams who need marketing momentum without a full in-house team.",
    challenge:
      "Marketing demands constant content, follow-up, and attention. Most businesses lose opportunities in the gaps — leads that never got a second touch.",
    capabilities: [
      "High-intent prospect discovery",
      "Campaign and content creation across platforms",
      "Automated follow-up with leads",
      "Vela, Veluro's AI growth agent",
      "Pipeline and business-health monitoring",
    ],
    providerNote:
      "Veluro's current offer page advertises a 7-day free trial with no credit card required. Confirm current terms on Veluro before signing up.",
    djcRole:
      "DJC Global introduces businesses to Veluro. The platform, support, and results are delivered by Veluro.",
    providerRole:
      "Veluro provides the platform, Vela, and customer support.",
    next: [
      "Share where your marketing stalls today",
      "We connect you with Veluro",
      "Explore the platform — their current offer page advertises a 7-day free trial",
      "Decide with full information",
    ],
    faqs: [
      {
        q: "Does AI replace our marketing team?",
        a: "No. Veluro is built to amplify whoever runs your marketing — an owner, a small team, or an agency.",
      },
      {
        q: "Is there a trial?",
        a: "Veluro's current offer page advertises a 7-day free trial with no credit card needed. Confirm current terms on their site.",
      },
    ],
    cta: { label: "Explore Veluro", url: "https://veluro.ai/djcmarketing" },
    provider: "Veluro",
    image:
      "https://images.unsplash.com/photo-1603302576837-37561b2e2302?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "A laptop running a vibrant AI software platform in a darkened studio",
  },
];

export const CONTEXT_IMAGE =
  "https://images.unsplash.com/photo-1531972111231-7482a960e109?auto=format&fit=crop&w=1600&q=80";
export const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1790856205969-04ec111bb412?auto=format&fit=crop&w=1600&q=80";
export const APPROACH_IMAGE =
  "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?auto=format&fit=crop&w=1600&q=80";
export const SOLUTIONS_IMAGE =
  "https://images.unsplash.com/photo-1789151753817-128c088ad4c5?auto=format&fit=crop&w=1600&q=80";
export const PANEL_IMAGES = {
  people:
    "https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?auto=format&fit=crop&w=1200&q=80",
  costs:
    "https://images.unsplash.com/photo-1601311852860-1d8f42381551?auto=format&fit=crop&w=1200&q=80",
  growth:
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80",
};

export const STEPS = [
  {
    num: "01",
    title: "Share what you want to improve",
    desc: "Tell us about your priorities in a short inquiry or a call. No preparation needed — plain language is perfect.",
  },
  {
    num: "02",
    title: "Identify a relevant solution",
    desc: "We help you see which of the seven destinations fits your situation — and which do not.",
  },
  {
    num: "03",
    title: "Connect with the specialist",
    desc: "We introduce you to the provider who delivers the solution, with context on what to ask.",
  },
  {
    num: "04",
    title: "Evaluate the details and decide",
    desc: "Pricing, eligibility, implementation, and delivery depend on the selected provider. You decide with full information.",
  },
];

export const HOME_FAQS = [
  {
    q: "Where should I start?",
    a: "Start with the problem, not the product. If you know the challenge — costs, benefits, capacity, growth — the Atlas lists the destinations that address it. If you would rather talk it through, send an inquiry or call. A short conversation usually points to one or two relevant solutions.",
  },
  {
    q: "Does DJC Global deliver these services directly?",
    a: "No. DJC Global connects you with specialized providers. Each provider delivers its own services, sets its own terms, and supports its own clients. Our role is to help you find a relevant one quickly.",
  },
  {
    q: "Can I explore more than one solution?",
    a: "Yes. The seven destinations are independent. Many businesses explore two at the same time — for example, technology costs and marketing.",
  },
  {
    q: "Do all solutions fit every business?",
    a: "No. Each solution has its own audience and eligibility. We will tell you plainly when something does not look like a fit, and each provider confirms the details that matter.",
  },
  {
    q: "What happens after I submit an inquiry?",
    a: "We review what you shared, identify the relevant specialist, and connect you — usually with context on what to ask. You are never required to submit a form before visiting a provider directly.",
  },
  {
    q: "Are savings or results guaranteed?",
    a: "No. Any savings, refunds, or results depend on the provider, your eligibility, and your circumstances. We avoid projecting outcomes we cannot stand behind.",
  },
];

export const solutionBySlug = (slug) => SOLUTIONS.find((s) => s.slug === slug);
